import React from "react";
import { Alert } from "./Alert";

/**
 * A render failure that stays inside the thing that failed (N35).
 *
 * React has one response to an exception thrown during render with no boundary
 * above it: it unmounts the entire root. Not the component, not the subtree —
 * the whole application, leaving a blank document with the URL still in the
 * bar. On 2026-09-19 that is exactly what a client saw, twice, when a
 * conversation found a FixyBee: `toProviderProps` called a `distanceLabel` that
 * a straight re-export had never bound into its module scope, and one
 * `ReferenceError` inside one card took the sidebar, the transcript, the
 * composer and the history with it. The conversation was still in the database
 * and every other widget in it would have rendered.
 *
 * So the rule the surface enforces from here on is narrower than "handle
 * errors": **one turn may not cost the screen.** Each widget renders inside one
 * of these, which is the granularity that keeps the sentence above it, the
 * turns around it and the composer below it alive, and the whole thread body
 * renders inside another as the backstop for a failure that is not a widget's.
 *
 * What is shown in place of the broken thing is deliberately small and
 * deliberately says what it was: a person photographing this screen should be
 * handing us the diagnosis, the same argument the error card's verbatim code
 * makes in `DanyThread`. `label` names the failing piece — the tool, for a
 * widget — and the message is the exception's own.
 *
 * `onError` exists so a caller can report; the console line is unconditional
 * because a boundary that swallows an exception silently trades a loud bug for
 * a quiet one, and the quiet one is the more expensive of the two.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[fixydany] render failed${this.props.label ? ` in ${this.props.label}` : ""}`,
      error, info?.componentStack,
    );
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps) {
    // A boundary that latches forever would keep a turn broken after the thing
    // that broke it is gone — a superseded draft card, a retried turn. The key
    // the caller passes is what says "this is a different thing now".
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <Alert tone="danger" title={this.props.title || "Something here did not render"}>
        <span style={{
          display: "block", fontFamily: "var(--font-mono)",
          fontSize: "var(--text-2xs)", color: "var(--text-muted)",
          userSelect: "all", overflowWrap: "anywhere",
        }} data-testid="render-error-detail">
          {this.props.label ? `${this.props.label} · ` : ""}
          {String(error?.message || error)}
        </span>
      </Alert>
    );
  }
}
